import type { QueueEntryResponse } from "@bw/shared/types/generated";
import { formatQueueDeadline } from "../lib/queueDeadline";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

function waitingPolicyCopy(entry: QueueEntryResponse): string | null {
  if (entry.waiting_reason === "awaiting_compatible_players") {
    return "Waiting for compatible players.";
  }
  if (
    entry.waiting_reason === "awaiting_target_occupancy" &&
    entry.predicted_assignment_type === "fresh"
  ) {
    return "Building a fresh court group.";
  }
  if (entry.waiting_reason === "awaiting_court_availability") {
    return "Waiting for a court to open.";
  }
  if (
    entry.waiting_reason === "assignment_pending" &&
    entry.predicted_assignment_type === "fresh"
  ) {
    return "Fresh court assignment in progress.";
  }
  if (
    entry.waiting_reason === "assignment_pending" &&
    entry.predicted_assignment_type === "top_off"
  ) {
    return "Joining an active court with enough time remaining.";
  }
  return null;
}

/**
 * Position + server-authored wait status for the waiting view.
 *
 * Honesty rules (REALISM):
 *   - `position` is the REAL 1-indexed place in line, shown as a numeral.
 *   - `waiting_until` wins when Auto policy provides a concrete deadline.
 *   - A fallback estimate is always rendered as approximate, never a promise.
 *     When neither value exists we show an em dash, never an invented number.
 */
export function QueuePositionCard({
  entry,
  queueAssignmentMode,
  assignmentModeUnavailable,
}: {
  entry: QueueEntryResponse;
  queueAssignmentMode: "manual" | "auto" | "self_serve" | null;
  assignmentModeUnavailable: boolean;
}) {
  const position = entry.position;
  const wait = entry.estimated_wait_minutes;
  const policyDeadline = formatQueueDeadline(entry.waiting_until);
  const isManual = queueAssignmentMode === "manual";
  const policyCopy = isManual ? null : waitingPolicyCopy(entry);

  let waitValue = "wait —";
  let waitNote = policyCopy ?? "We can't estimate the wait yet.";
  let waitDateTime: string | undefined;
  if (assignmentModeUnavailable) {
    waitNote = "Assignment mode unavailable.";
  } else if (queueAssignmentMode === null) {
    waitNote = "Checking assignment mode.";
  } else if (isManual) {
    waitValue = position === 1 ? "Next" : `Position ${position ?? "-"}`;
    waitNote = "Waiting for staff assignment.";
  } else if (policyDeadline && entry.waiting_until) {
    waitValue = `By ${policyDeadline}`;
    waitNote = policyCopy ?? "Auto-assignment policy deadline.";
    waitDateTime = entry.waiting_until;
  } else if (wait != null) {
    waitValue = `~${wait} min`;
    waitNote = policyCopy ?? "Estimated wait — a rough guide, not a promise.";
  }

  return (
    <Paper
      variant="outlined"
      role="group"
      aria-label="Your place in the queue"
      sx={{ p: 2 }}
    >
      <Typography variant="overline" color="primary.main">
        In the queue
      </Typography>
      {/* The position numeral + label live in a polite live region so a
          screen-reader user hears the ~5s position change ("Position 1, you're
          next up") — the single most important signal on this surface. The
          numeral is NOT aria-hidden here: the live region must announce it. */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={3}
        aria-live="polite"
        sx={{ mt: 1, justifyContent: "space-between" }}
      >
        <Stack spacing={0.25}>
          <Typography variant="h2" sx={{ fontWeight: 900, lineHeight: 1 }}>
            {position ?? "—"}
          </Typography>
          <Typography color="text.secondary">
            {position != null
              ? position === 1
                ? "You're next up"
                : `Position in line`
              : "Holding your place"}
          </Typography>
        </Stack>
        <Stack
          spacing={0.25}
          sx={{ textAlign: { sm: "right" }, maxWidth: 360 }}
        >
          {waitDateTime ? (
            <Typography
              component="time"
              dateTime={waitDateTime}
              sx={{ fontWeight: 800 }}
            >
              {waitValue}
            </Typography>
          ) : (
            <Typography sx={{ fontWeight: 800 }}>{waitValue}</Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {waitNote}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}
